import { DeliveryTimeSlot, DeliveryTimeSlotMap } from '@constants'
import { type CheckoutOrder } from '@hooks-api/types/dataModel/orders'
import { Radio } from '@mui/material'
import { Box } from '@pets-ui/Box/Box'
import { Button } from '@pets-ui/Button/Button'
import { DialogHeader } from '@pets-ui/DialogHeader/DialogHeader'
import { HStack } from '@pets-ui/HStack/HStack'
import { ResponsiveDialog } from '@pets-ui/ResponsiveDialog/ResponsiveDialog'
import { Text } from '@pets-ui/Text/Text'
import { colors } from '@pets-ui/tokens/colors.stylex'
import { useResponsive } from '@pets-ui/useResponsive/useResponsive'
import { VStack } from '@pets-ui/VStack/VStack'
import * as stylex from '@stylexjs/stylex'
import { useEffect, useRef } from 'react'
import { Controller, useFormContext } from 'react-hook-form'

const dialogAndDrawer = stylex.create({
  root: {
    padding: 16,
  },
  radio: {
    padding: 16,
    borderColor: colors.grey300,
    borderRadius: 4,
    borderStyle: 'solid',
    borderWidth: 1,
    alignItems: 'center',
    backgroundColor: colors.white,
    cursor: 'pointer',
  },
})

const footer = stylex.create({
  root: {
    padding: '12px 16px',
    display: 'flex',
    justifyContent: 'flex-end',
  },
  button: {
    backgroundColor: colors.black,
    color: colors.white,
  },
})

const radioSxProps = {
  sx: {
    'color': colors.grey400,
    '&.Mui-checked': {
      color: colors.black,
    },
  },
}

const Footer = ({ onSubmit }: { onSubmit: DialogDeliveryTimeSlotProps['onSubmit'] }) => {
  const isMobile = useResponsive('mobile')
  return (
    <Box rootStyle={footer.root}>
      <Button variant="filled" rootStyle={footer.button} onClick={onSubmit} fullWidth={isMobile}>
        確認
      </Button>
    </Box>
  )
}

const DialogDeliveryTimeSlotContent = () => {
  const { watch, control, setValue } = useFormContext<CheckoutOrder>()
  const formValues = watch()

  const handleChangeDeliveryTimeSlot = (value: DeliveryTimeSlot) => {
    setValue('delivery_time_slot', value)
  }

  return (
    <Box rootStyle={dialogAndDrawer.root}>
      <VStack gap={4}>
        <HStack
          rootStyle={dialogAndDrawer.radio}
          onClick={() => handleChangeDeliveryTimeSlot(DeliveryTimeSlot.MORNING)}
        >
          <Controller
            control={control}
            name="delivery_time_slot"
            render={({ field }) => (
              <Radio
                {...field}
                {...radioSxProps}
                size="small"
                value={DeliveryTimeSlot.MORNING}
                checked={formValues.delivery_time_slot === DeliveryTimeSlot.MORNING}
              />
            )}
          />
          <Text variant="body" color={colors.black}>
            {DeliveryTimeSlotMap[DeliveryTimeSlot.MORNING]}
          </Text>
        </HStack>
        <HStack
          rootStyle={dialogAndDrawer.radio}
          onClick={() => handleChangeDeliveryTimeSlot(DeliveryTimeSlot.AFTERNOON)}
        >
          <Controller
            control={control}
            name="delivery_time_slot"
            render={({ field }) => (
              <Radio
                {...field}
                {...radioSxProps}
                size="small"
                value={DeliveryTimeSlot.AFTERNOON}
                checked={formValues.delivery_time_slot === DeliveryTimeSlot.AFTERNOON}
              />
            )}
          />
          <Text variant="body" color={colors.black}>
            {DeliveryTimeSlotMap[DeliveryTimeSlot.AFTERNOON]}
          </Text>
        </HStack>
      </VStack>
    </Box>
  )
}

interface DialogDeliveryTimeSlotProps {
  open: boolean
  onClose: () => void
  onSubmit: () => void
}

export function DialogDeliveryTimeSlot({ open, onClose, onSubmit }: DialogDeliveryTimeSlotProps) {
  const { watch, setValue } = useFormContext<CheckoutOrder>()
  const formValues = watch()
  const deliveryTimeRef = useRef<DeliveryTimeSlot>(formValues?.delivery_time_slot)

  useEffect(() => {
    if (open) return
    deliveryTimeRef.current = formValues?.delivery_time_slot
  }, [open, formValues])

  const handleReset = () => {
    setValue('delivery_time_slot', deliveryTimeRef.current)
  }

  const handleClose = () => {
    handleReset()
    onClose()
  }

  return (
    <ResponsiveDialog open={open} fullWidth>
      <DialogHeader
        title="收貨時間"
        onClose={handleClose}
      />
      <DialogDeliveryTimeSlotContent />
      <Footer onSubmit={onSubmit} />
    </ResponsiveDialog>
  )
}
